import React, { useState, useEffect } from 'react';
import { FiX, FiUser, FiPhone, FiMail, FiCalendar, FiMessageSquare, FiEdit, FiUserPlus, FiCheckCircle, FiGift, FiFileText } from 'react-icons/fi';
import api from '../../utils/api';
import { TicketStatusBadge, PriorityBadge, CategoryBadge, SlaIndicator } from './SupportStatusBadge';
import ChatHistory from './ChatHistory';
import SendMessageModal from './SendMessageModal';
import AddNoteModal from './AddNoteModal';
import ResolveTicketModal from './ResolveTicketModal';

const TicketDetailModal = ({ ticket, onClose, onUpdate }) => {
  const [ticketDetails, setTicketDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('conversation');
  const [agents, setAgents] = useState([]);

  // Action modal states
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolveWithCoupon, setResolveWithCoupon] = useState(false);

  // Status update state
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [updatingPriority, setUpdatingPriority] = useState(false);
  const [assigningAgent, setAssigningAgent] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState('');

  // Fetch ticket details
  const fetchTicketDetails = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/support/tickets/${ticket._id}`);
      if (response.data.success) {
        setTicketDetails(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching ticket details:', error);
      alert('Failed to load ticket details');
    } finally {
      setLoading(false);
    }
  };

  // Fetch agents
  const fetchAgents = async () => {
    try {
      const response = await api.get('/admin/agents');
      if (response.data.success) {
        setAgents(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  useEffect(() => {
    fetchTicketDetails();
    fetchAgents();
  }, [ticket._id]);

  useEffect(() => {
    if (ticketDetails?.ticket?.assignedTo?._id) {
      setSelectedAgent(ticketDetails.ticket.assignedTo._id);
    }
  }, [ticketDetails]);

  // Update status
  const updateStatus = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      const response = await api.patch(`/support/tickets/${ticket._id}/status`, { status: newStatus });
      if (response.data.success) {
        fetchTicketDetails();
        onUpdate();
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert(error.response?.data?.message || 'Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Update priority
  const updatePriority = async (newPriority) => {
    setUpdatingPriority(true);
    try {
      const response = await api.patch(`/support/tickets/${ticket._id}/priority`, { priority: newPriority });
      if (response.data.success) {
        fetchTicketDetails();
        onUpdate();
      }
    } catch (error) {
      console.error('Error updating priority:', error);
      alert(error.response?.data?.message || 'Failed to update priority');
    } finally {
      setUpdatingPriority(false);
    }
  };

  // Assign agent
  const assignAgent = async () => {
    if (!selectedAgent) return;
    setAssigningAgent(true);
    try {
      const response = await api.patch(`/support/tickets/${ticket._id}/assign`, { agentId: selectedAgent });
      if (response.data.success) {
        fetchTicketDetails();
        onUpdate();
      }
    } catch (error) {
      console.error('Error assigning agent:', error);
      alert(error.response?.data?.message || 'Failed to assign agent');
    } finally {
      setAssigningAgent(false);
    }
  };

  // Close ticket
  const closeTicket = async () => {
    if (!window.confirm('Are you sure you want to close this ticket?')) return;
    try {
      const response = await api.post(`/support/tickets/${ticket._id}/close`);
      if (response.data.success) {
        fetchTicketDetails();
        onUpdate();
        alert('Ticket closed successfully');
      }
    } catch (error) {
      console.error('Error closing ticket:', error);
      alert(error.response?.data?.message || 'Failed to close ticket');
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle action success
  const handleActionSuccess = () => {
    fetchTicketDetails();
    onUpdate();
  };

  const ticketData = ticketDetails?.ticket || ticket;
  const chatHistory = ticketDetails?.chatHistory || [];

  const tabs = [
    { id: 'conversation', label: 'Conversation', icon: <FiMessageSquare size={16} /> },
    { id: 'details', label: 'Details', icon: <FiFileText size={16} /> },
    { id: 'actions', label: 'Actions', icon: <FiEdit size={16} /> }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-start p-6 border-b border-gray-200 bg-gray-50">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-xl font-semibold text-gray-900">{ticketData.ticketNumber}</h2>
              <TicketStatusBadge status={ticketData.status} />
              <PriorityBadge priority={ticketData.priority} />
              <SlaIndicator sla={ticketData.sla} status={ticketData.status} />
            </div>
            <p className="text-gray-500 text-sm">
              Created: {formatDate(ticketData.createdAt)}
              {ticketData.assignedTo && (
                <span> | Assigned to: <strong>{ticketData.assignedTo.name}</strong></span>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-gray-500">Loading ticket details...</span>
            </div>
          ) : (
            <>
              {/* Conversation Tab */}
              {activeTab === 'conversation' && (
                <div>
                  {/* Issue Description */}
                  <div className="bg-blue-50 rounded-lg p-4 mb-4 border border-blue-100">
                    <h4 className="font-semibold text-blue-800 mb-2">Issue Description</h4>
                    <p className="text-gray-700">{ticketData.issueDescription || 'No description provided'}</p>
                    {ticketData.escalationReason && (
                      <p className="text-sm text-blue-600 mt-2">
                        Escalation Reason: {ticketData.escalationReason.replace(/_/g, ' ')}
                      </p>
                    )}
                  </div>

                  {/* Chat History */}
                  <ChatHistory
                    chatHistory={chatHistory}
                    agentMessages={ticketData.agentMessages}
                    userResponses={ticketData.userResponses}
                    internalNotes={ticketData.internalNotes}
                  />

                  {/* Quick Actions */}
                  <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => setShowMessageModal(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <FiMessageSquare size={16} />
                      Send Message
                    </button>
                    <button
                      onClick={() => setShowNoteModal(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
                    >
                      <FiEdit size={16} />
                      Add Note
                    </button>
                    {ticketData.status !== 'resolved' && ticketData.status !== 'closed' && (
                      <>
                        <button
                          onClick={() => { setResolveWithCoupon(false); setShowResolveModal(true); }}
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                          <FiCheckCircle size={16} />
                          Resolve
                        </button>
                        <button
                          onClick={() => { setResolveWithCoupon(true); setShowResolveModal(true); }}
                          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                        >
                          <FiGift size={16} />
                          Resolve with Coupon
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Details Tab */}
              {activeTab === 'details' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* User Information */}
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <FiUser size={18} />
                      User Information
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm text-gray-500">Name</p>
                        <p className="font-medium">{ticketData.userId?.name || 'N/A'}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <FiPhone size={14} className="text-gray-400" />
                        <span>{ticketData.userId?.phoneNumber || 'N/A'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <FiMail size={14} className="text-gray-400" />
                        <span>{ticketData.userId?.email || 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Vendor Information (if applicable) */}
                  {ticketData.vendorId && (
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <FiUser size={18} />
                        Vendor Information
                      </h4>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm text-gray-500">Store Name</p>
                          <p className="font-medium">{ticketData.vendorId.store_name || 'N/A'}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <FiPhone size={14} className="text-gray-400" />
                          <span>{ticketData.vendorId.phone || 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <FiMail size={14} className="text-gray-400" />
                          <span>{ticketData.vendorId.email || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Booking Information (if applicable) */}
                  {ticketData.bookingId && (
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <FiCalendar size={18} />
                        Related Booking
                      </h4>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm text-gray-500">Booking Number</p>
                          <p className="font-medium text-blue-600">{ticketData.bookingId.bookingNumber || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Booking Date</p>
                          <p className="font-medium">{formatDate(ticketData.bookingId.bookingDate)}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Ticket Information */}
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <FiFileText size={18} />
                      Ticket Information
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm text-gray-500">Category</p>
                        <CategoryBadge category={ticketData.category} />
                        {ticketData.subCategory && (
                          <span className="ml-2 text-sm text-gray-600">{ticketData.subCategory}</span>
                        )}
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Escalation Reason</p>
                        <p className="font-medium">{ticketData.escalationReason?.replace(/_/g, ' ') || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Created At</p>
                        <p className="font-medium">{formatDate(ticketData.createdAt)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Last Updated</p>
                        <p className="font-medium">{formatDate(ticketData.updatedAt)}</p>
                      </div>
                    </div>
                  </div>

                  {/* SLA Information */}
                  {ticketData.sla && (
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <FiCalendar size={18} />
                        SLA Information
                      </h4>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm text-gray-500">Response Deadline</p>
                          <p className="font-medium">{formatDate(ticketData.sla.responseDeadline)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Resolution Deadline</p>
                          <p className="font-medium">{formatDate(ticketData.sla.resolutionDeadline)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">First Response At</p>
                          <p className="font-medium">{ticketData.sla.firstResponseAt ? formatDate(ticketData.sla.firstResponseAt) : 'Not yet responded'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">SLA Breached</p>
                          <p className={`font-medium ${ticketData.sla.breached ? 'text-red-600' : 'text-green-600'}`}>
                            {ticketData.sla.breached ? 'Yes' : 'No'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Resolution Information (if resolved) */}
                  {ticketData.resolution && (
                    <div className="bg-green-50 rounded-lg p-4 border border-green-200 md:col-span-2">
                      <h4 className="font-semibold text-green-800 mb-4 flex items-center gap-2">
                        <FiCheckCircle size={18} />
                        Resolution
                      </h4>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm text-green-600">Resolution Type</p>
                          <p className="font-medium">{ticketData.resolution.type?.replace(/_/g, ' ') || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-green-600">Description</p>
                          <p className="font-medium">{ticketData.resolution.description || 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Chatbot Context (if available) */}
                  {ticketData.chatbotContext && (
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 md:col-span-2">
                      <h4 className="font-semibold text-gray-800 mb-4">Chatbot Context</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <p className="text-sm text-gray-500">Flow Path</p>
                          <p className="font-medium text-sm">{ticketData.chatbotContext.flowPath?.join(' → ') || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Message Count</p>
                          <p className="font-medium">{ticketData.chatbotContext.messageCount || 0}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Failed Attempts</p>
                          <p className="font-medium">{ticketData.chatbotContext.failedAttempts || 0}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Actions Tab */}
              {activeTab === 'actions' && (
                <div className="space-y-6">
                  {/* Update Status */}
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h4 className="font-semibold text-gray-800 mb-4">Update Status</h4>
                    <div className="flex flex-wrap gap-2">
                      {['open', 'assigned', 'in_progress', 'pending_user', 'pending_vendor', 'resolved', 'closed'].map(status => (
                        <button
                          key={status}
                          onClick={() => updateStatus(status)}
                          disabled={updatingStatus || ticketData.status === status}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            ticketData.status === status
                              ? 'bg-blue-600 text-white'
                              : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                          } ${updatingStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Update Priority */}
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h4 className="font-semibold text-gray-800 mb-4">Update Priority</h4>
                    <div className="flex flex-wrap gap-2">
                      {['low', 'medium', 'high', 'urgent'].map(priority => (
                        <button
                          key={priority}
                          onClick={() => updatePriority(priority)}
                          disabled={updatingPriority || ticketData.priority === priority}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            ticketData.priority === priority
                              ? 'bg-blue-600 text-white'
                              : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                          } ${updatingPriority ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {priority.charAt(0).toUpperCase() + priority.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Assign Agent */}
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <FiUserPlus size={18} />
                      Assign Agent
                    </h4>
                    <div className="flex gap-2">
                      <select
                        value={selectedAgent}
                        onChange={(e) => setSelectedAgent(e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select Agent</option>
                        {agents.map(agent => (
                          <option key={agent._id} value={agent._id}>{agent.name}</option>
                        ))}
                      </select>
                      <button
                        onClick={assignAgent}
                        disabled={!selectedAgent || assigningAgent}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {assigningAgent ? 'Assigning...' : 'Assign'}
                      </button>
                    </div>
                  </div>

                  {/* Close Ticket */}
                  {ticketData.status !== 'closed' && (
                    <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                      <h4 className="font-semibold text-red-800 mb-4">Close Ticket</h4>
                      <p className="text-sm text-red-600 mb-4">
                        Closing a ticket will mark it as completed. This action should be done after the issue is fully resolved.
                      </p>
                      <button
                        onClick={closeTicket}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                      >
                        Close Ticket
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Action Modals */}
      {showMessageModal && (
        <SendMessageModal
          ticketId={ticket._id}
          onClose={() => setShowMessageModal(false)}
          onSuccess={handleActionSuccess}
        />
      )}

      {showNoteModal && (
        <AddNoteModal
          ticketId={ticket._id}
          onClose={() => setShowNoteModal(false)}
          onSuccess={handleActionSuccess}
        />
      )}

      {showResolveModal && (
        <ResolveTicketModal
          ticketId={ticket._id}
          withCoupon={resolveWithCoupon}
          onClose={() => setShowResolveModal(false)}
          onSuccess={handleActionSuccess}
        />
      )}
    </div>
  );
};

export default TicketDetailModal;
